import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { absoluteUrl } from "@/lib/site";
import { submitCustomLicenseRequest } from "@/lib/bkls.functions";
import { validateEmail } from "@/lib/email-validation";
import { beatsCatalogQuery } from "./beats.index";

export const Route = createFileRoute("/beats/custom")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(beatsCatalogQuery);
    return null;
  },
  head: () => ({
    meta: [
      { title: "Custom Licensing Request · Beats · Buddha KA" },
      {
        name: "description",
        content:
          "Request bespoke Buddha KA beat licensing for film, sync, brand campaigns, exclusive use or unusual territories.",
      },
      { property: "og:title", content: "Custom Licensing Request · Buddha KA Beats" },
      { property: "og:description", content: "Bespoke terms for film, sync, brand and exclusive use." },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/beats/custom") }],
  }),
  component: CustomRequest,
});

function CustomRequest() {
  const beats = useQuery(beatsCatalogQuery);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<{ reference: string; confirmationSent: boolean } | null>(null);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError(null);

    const emailCheck = validateEmail(String(form.get("email") ?? ""));
    if (!emailCheck.ok) {
      setEmailError(emailCheck.error ?? "Enter a valid email address.");
      (event.currentTarget.elements.namedItem("email") as HTMLInputElement | null)?.focus();
      return;
    }
    setEmailError(null);
    setState("sending");
    const budget = String(form.get("budget") ?? "").trim();
    const result = await submitCustomLicenseRequest({
      data: {
        contactName: String(form.get("name") ?? ""),
        contactEmail: emailCheck.normalized,
        organization: String(form.get("organization") ?? ""),
        beatSlug: String(form.get("beat") ?? ""),
        intendedUse: String(form.get("use") ?? ""),
        ...(budget ? { budgetCents: Math.round(Number(budget) * 100) } : {}),
        timeline: String(form.get("timeline") ?? ""),
        message: String(form.get("message") ?? ""),
      },
    }).catch(() => ({ error: "Could not send your request. Please check the form and try again." }));

    if ("error" in result) {
      setError(result.error);
      setState("idle");
      return;
    }
    setReceipt({ reference: result.reference, confirmationSent: result.confirmationSent });
    setState("sent");
  }

  const field =
    "mt-2 w-full rounded-xl border hairline bg-charcoal px-4 py-3 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none";
  const label = "block text-[10px] uppercase tracking-[0.2em] text-gold/80";

  return (
    <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Custom licensing</p>
        <h1 className="mt-4 font-serif text-5xl">Tell us the use.</h1>
        <p className="mt-5 text-muted-foreground">
          Film, sync, brand campaigns, exclusive use, unusual territories — anything the standard tiers do not
          cover. You will receive written terms and a quote. Nothing is licensed until both sides agree in
          writing.
        </p>
      </header>

      {state === "sent" ? (
        <div className="mt-12 rounded-2xl border hairline bg-charcoal p-8">
          <h2 className="font-serif text-2xl text-gold-luminous">Request received.</h2>
          {receipt?.reference ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Your reference is{" "}
              <span className="text-gold-luminous">{receipt.reference}</span> — quote it in any reply.
            </p>
          ) : null}
          <p className="mt-3 text-sm text-muted-foreground">
            {receipt?.confirmationSent === false
              ? "We have your request. A confirmation email could not be delivered right now, but nothing is lost — we will respond to the address you provided."
              : "A confirmation email is on its way. We will respond to the email address you provided."}{" "}
            If you have an account, the request also appears in your Beats account history.
          </p>
          <Link to="/beats" className="mt-6 inline-flex text-sm text-gold hover:text-gold-luminous">
            ← Back to Beats
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-12 space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label className={label} htmlFor="name">
                Your name
              </label>
              <input id="name" name="name" required maxLength={120} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                maxLength={255}
                className={field}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? "email-error" : undefined}
                onChange={() => emailError && setEmailError(null)}
              />
              {emailError && (
                <p id="email-error" role="alert" className="mt-2 text-xs text-red-400">
                  {emailError}
                </p>
              )}
            </div>
            <div>
              <label className={label} htmlFor="organization">
                Company or project (optional)
              </label>
              <input id="organization" name="organization" maxLength={160} className={field} />
            </div>
            <div>
              <label className={label} htmlFor="beat">
                Beat (optional)
              </label>
              <select id="beat" name="beat" className={field}>
                <option value="">Not specific / to be selected</option>
                {(beats.data ?? []).map((b) => (
                  <option key={b.slug} value={b.slug}>
                    {b.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label} htmlFor="budget">
                Budget in USD (optional)
              </label>
              <input id="budget" name="budget" type="number" min="0" step="1" className={field} />
            </div>
            <div>
              <label className={label} htmlFor="timeline">
                Timeline (optional)
              </label>
              <input id="timeline" name="timeline" maxLength={200} className={field} placeholder="e.g. delivery by March" />
            </div>
          </div>

          <div>
            <label className={label} htmlFor="use">
              Intended use
            </label>
            <textarea
              id="use"
              name="use"
              required
              minLength={10}
              maxLength={1000}
              rows={4}
              className={field}
              placeholder="Where the music will appear, for how long, in which territories, and how it will be monetized."
            />
          </div>

          <div>
            <label className={label} htmlFor="message">
              Anything else (optional)
            </label>
            <textarea id="message" name="message" maxLength={4000} rows={4} className={field} />
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={state === "sending"}
            className="rounded-full border border-gold px-6 py-3 text-sm font-medium text-gold-luminous transition-all hover:bg-gold hover:text-obsidian disabled:opacity-50"
          >
            {state === "sending" ? "Sending…" : "Send request"}
          </button>
          <p className="text-xs text-muted-foreground">
            Submitting a request does not grant any licence or permission to use the music.
          </p>
        </form>
      )}
    </div>
  );
}
